// A pose for merged part geometry (partGeometry.ts): one bone per rigid part or chain ring. Bones
// live outside the scene graph; callers write each part's rig-local matrix straight into
// `bone.matrixWorld`, and three.js skinning does the rest (the renderer calls `skeleton.update()`
// once per frame). Meshes use the detached bind mode with an identity bind matrix, so a vertex's
// final position is objectMatrixWorld × partMatrix × bindPosition.
import {
  Bone, DetachedBindMode, Matrix4, Skeleton, SkinnedMesh, type BufferGeometry, type Material,
} from 'three';

const IDENTITY = new Matrix4();
const ZERO = new Matrix4().makeScale(0, 0, 0);

export class PartSkin {
  readonly bones: Bone[];
  readonly skeleton: Skeleton;
  /** The bones' matrices as plain arrays, for allocation-free writes. */
  private readonly els: number[][];

  constructor(count: number) {
    this.bones = [];
    for (let i = 0; i < count; i++) {
      const b = new Bone();
      b.matrixAutoUpdate = false;
      b.matrixWorldAutoUpdate = false;
      this.bones.push(b);
    }
    const inverses: Matrix4[] = [];
    for (let i = 0; i < count; i++) inverses.push(new Matrix4());
    this.skeleton = new Skeleton(this.bones, inverses);
    this.els = this.bones.map((b) => b.matrixWorld.elements);
  }

  /** A skinned mesh of `geometry` posed by this skin. */
  mesh(geometry: BufferGeometry, material: Material | Material[], name: string): SkinnedMesh {
    const m = new SkinnedMesh(geometry, material);
    m.name = name;
    m.bindMode = DetachedBindMode;
    m.bind(this.skeleton, IDENTITY);
    if (geometry.boundingSphere) m.boundingSphere = geometry.boundingSphere.clone();
    return m;
  }

  set(i: number, m: Matrix4): void {
    this.bones[i].matrixWorld.copy(m);
  }

  hide(i: number): void {
    this.bones[i].matrixWorld.copy(ZERO);
  }

  /** Translation + uniform scale (no rotation). */
  setTS(i: number, x: number, y: number, z: number, s: number): void {
    const e = this.els[i];
    e[0] = s; e[1] = 0; e[2] = 0; e[3] = 0;
    e[4] = 0; e[5] = s; e[6] = 0; e[7] = 0;
    e[8] = 0; e[9] = 0; e[10] = s; e[11] = 0;
    e[12] = x; e[13] = y; e[14] = z; e[15] = 1;
  }

  /**
   * A chain ring: center c, tangent t (unit), radius r. The ring's local x/y axes are any frame
   * perpendicular to t; consecutive rings use a parallel-transported frame via `refX/Y/Z`.
   */
  setRing(i: number, cx: number, cy: number, cz: number, tx: number, ty: number, tz: number, nx: number, ny: number, nz: number, r: number): void {
    // b = t × n
    const bx = ty * nz - tz * ny;
    const by = tz * nx - tx * nz;
    const bz = tx * ny - ty * nx;
    const e = this.els[i];
    e[0] = nx * r; e[1] = ny * r; e[2] = nz * r; e[3] = 0;
    e[4] = bx * r; e[5] = by * r; e[6] = bz * r; e[7] = 0;
    e[8] = tx; e[9] = ty; e[10] = tz; e[11] = 0;
    e[12] = cx; e[13] = cy; e[14] = cz; e[15] = 1;
  }
}
